import { TrendingDown } from 'lucide-react';
import { useState } from 'react';
import { Async } from '../../components/Async.tsx';
import { DataTable, type Column } from '../../components/DataTable.tsx';
import { EmptyState, Panel } from '../../components/States.tsx';
import { SegmentedControl, SegmentedItem } from '../../components/ui/toggle-group.tsx';
import {
  DASH,
  formatMoney,
  formatMultiple,
  formatNumber,
  formatPercent,
} from '../../lib/format.ts';
import { major, pairMetric } from '../../lib/home.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import { useAds, type AdLevel, type AdsResponse } from '../../lib/queries.ts';
import type { DashboardParams } from '../../lib/url-state.ts';
import { LegendKey } from './HourlyCallsChart.tsx';
import { HomeSection, StatStrip, StatTile } from './StatTile.tsx';

type Ads = AdsResponse['data'];
type Row = Ads['rows'][number];
type Totals = Ads['totals'];

const CPL_COLOR = 'var(--chart-1)';
const CAC_COLOR = 'var(--chart-6)';

/** ROAS below 1x lost money; shown in red with a word, not by colour alone. */
function Roas({ value, coverage }: { value: number | null; coverage: number | null }) {
  const t = useT();
  const locale = useLocale();
  if (value === null) return <>{DASH}</>;
  const lowerBound = coverage !== null && coverage < 1;
  const text = `${lowerBound ? '≥ ' : ''}${formatMultiple(value, locale)}`;
  if (value >= 1 || lowerBound)
    return <span title={lowerBound ? t.home.kpi.roas.lowerBound : undefined}>{text}</span>;
  return (
    <span className="inline-flex items-center gap-1 font-medium text-destructive">
      <TrendingDown aria-hidden="true" className="size-3.5" />
      {text}
      <span className="sr-only">({t.home.ads.loss})</span>
    </span>
  );
}

export function AdsSection({ params, showDelta }: { params: DashboardParams; showDelta: boolean }) {
  const t = useT();
  const locale = useLocale();
  const a = t.home.ads;
  const k = t.home.kpi;
  const [level, setLevel] = useState<AdLevel>('campaign');
  const table = useAds(params, level);
  const bySet = useAds(params, 'ad_set');
  const data = bySet.data?.data;
  const totals = data?.totals;
  const previous = data?.previousTotals ?? null;
  const currency = data?.currency ?? 'USD';
  const money = (minor: number | null, cents?: boolean) =>
    formatMoney(minor, currency, locale, cents === undefined ? {} : { cents });

  return (
    <HomeSection id="home-ads" title={t.home.sections.ads} hint={t.home.sectionHints.ads}>
      <StatStrip columns={5}>
        <StatTile
          label={k.spend.name}
          formatAmount={(v) => money(v * 100, false)}
          definition={k.spend.definition}
          value={totals ? money(totals.spendMinor, false) : undefined}
          metric={
            totals
              ? pairMetric(major(totals.spendMinor), major(previous?.spendMinor ?? null), 'money')
              : undefined
          }
          showDelta={showDelta}
        />
        <StatTile
          label={k.ctr.name}
          definition={k.ctr.definition}
          value={totals ? formatPercent(totals.ctr, locale, 2) : undefined}
          metric={
            totals ? pairMetric(totals.ctr, previous?.ctr ?? null, 'ratio', 'rate') : undefined
          }
          showDelta={showDelta}
        />
        <StatTile
          label={k.cpl.name}
          formatAmount={(v) => money(v * 100, true)}
          definition={k.cpl.definition}
          value={totals ? money(totals.cplMinor, true) : undefined}
          metric={
            totals
              ? pairMetric(major(totals.cplMinor), major(previous?.cplMinor ?? null), 'money')
              : undefined
          }
          showDelta={showDelta}
          note={
            totals?.leads !== null && totals
              ? `${formatNumber(totals.leads, locale)} ${t.units.leads}`
              : undefined
          }
        />
        <StatTile
          label={k.cac.name}
          formatAmount={(v) => money(v * 100, false)}
          definition={k.cac.definition}
          value={totals ? money(totals.cacMinor, false) : undefined}
          metric={
            totals
              ? pairMetric(major(totals.cacMinor), major(previous?.cacMinor ?? null), 'money')
              : undefined
          }
          showDelta={showDelta}
          note={
            totals?.sales !== null && totals
              ? `${formatNumber(totals.sales, locale)} ${t.units.deals}`
              : undefined
          }
        />
        <StatTile
          label={k.roas.name}
          definition={k.roas.definition}
          value={
            totals
              ? `${totals.revenueCoverage !== null && totals.revenueCoverage < 1 ? '≥ ' : ''}${formatMultiple(totals.roas, locale)}`
              : undefined
          }
          metric={
            totals ? pairMetric(totals.roas, previous?.roas ?? null, 'ratio', 'rate') : undefined
          }
          showDelta={false}
          note={
            totals?.revenueCoverage !== null && totals && totals.revenueCoverage < 1
              ? k.roas.lowerBound
              : undefined
          }
        />
      </StatStrip>

      <div className="space-y-4">
        <Panel
          title={a.title}
          className="min-w-0"
          actions={
            <SegmentedControl
              aria-label={a.level}
              value={level}
              onValueChange={(value) => {
                if (value) setLevel(value as AdLevel);
              }}
            >
              <SegmentedItem value="campaign">{a.levels.campaign}</SegmentedItem>
              <SegmentedItem value="ad_set">{a.levels.ad_set}</SegmentedItem>
              <SegmentedItem value="ad">{a.levels.ad}</SegmentedItem>
            </SegmentedControl>
          }
        >
          <Async query={table}>
            {({ data: d }) =>
              d.rows.length === 0 ? (
                <EmptyState title={t.common.noData} description={a.empty} />
              ) : (
                <AdsTable data={d} />
              )
            }
          </Async>
        </Panel>
        <Panel title={a.chartTitle} className="min-w-0">
          <Async query={bySet}>
            {({ data: d }) =>
              d.rows.length === 0 ? (
                <EmptyState title={t.common.noData} description={a.empty} />
              ) : (
                <CostBars rows={d.rows} currency={d.currency} />
              )
            }
          </Async>
        </Panel>
      </div>
    </HomeSection>
  );
}

function AdsTable({ data }: { data: Ads }) {
  const t = useT();
  const locale = useLocale();
  const a = t.home.ads;
  const c = a.columns;
  const money = (minor: number | null, cents?: boolean) =>
    formatMoney(minor, data.currency, locale, cents === undefined ? {} : { cents });
  const n = (value: number | null) => formatNumber(value, locale);
  const anyUnknown = data.rows.some((r) => r.leads === null);

  const columns: Column<Row>[] = [
    {
      id: 'name',
      header: c.name,
      cell: (r) => (
        <span className="block max-w-64">
          <span className="block truncate" title={r.name}>
            {r.name}
          </span>
          <span className="block truncate text-xs font-normal text-muted-foreground">
            {r.platform === 'google' ? 'Google' : 'Meta'}
            {r.level !== 'campaign'
              ? ` · ${r.adSetName ? `${r.adSetName} · ` : ''}${r.campaignName}`
              : ''}
          </span>
        </span>
      ),
      sortValue: (r) => r.name,
    },
    {
      id: 'spend',
      header: c.spend,
      align: 'right',
      cell: (r) => money(r.spendMinor, false),
      sortValue: (r) => r.spendMinor,
    },
    {
      id: 'impressions',
      header: c.impressions,
      align: 'right',
      cell: (r) => n(r.impressions),
      sortValue: (r) => r.impressions,
    },
    {
      id: 'clicks',
      header: c.clicks,
      align: 'right',
      cell: (r) => n(r.clicks),
      sortValue: (r) => r.clicks,
    },
    {
      id: 'ctr',
      header: c.ctr,
      align: 'right',
      cell: (r) => formatPercent(r.ctr, locale, 2),
      sortValue: (r) => r.ctr,
    },
    {
      id: 'leads',
      header: c.leads,
      align: 'right',
      cell: (r) => n(r.leads),
      sortValue: (r) => r.leads,
    },
    {
      id: 'cpl',
      header: c.cpl,
      align: 'right',
      cell: (r) => money(r.cplMinor, true),
      sortValue: (r) => r.cplMinor,
    },
    {
      id: 'sales',
      header: c.sales,
      align: 'right',
      cell: (r) => n(r.sales),
      sortValue: (r) => r.sales,
    },
    {
      id: 'cac',
      header: c.cac,
      align: 'right',
      cell: (r) => money(r.cacMinor, false),
      sortValue: (r) => r.cacMinor,
    },
    {
      id: 'revenue',
      header: c.revenue,
      align: 'right',
      cell: (r) => money(r.revenueMinor, false),
      sortValue: (r) => r.revenueMinor,
    },
    {
      id: 'roas',
      header: c.roas,
      align: 'right',
      help: t.home.kpi.roas.definition,
      cell: (r) => <Roas value={r.roas} coverage={r.revenueCoverage} />,
      sortValue: (r) => r.roas,
    },
  ];
  const tt: Totals = data.totals;

  return (
    <div className="space-y-3">
      <DataTable
        caption={a.title}
        columns={columns}
        rows={data.rows}
        rowKey={(r) => r.key}
        defaultSort={{ id: 'spend', direction: 'desc' }}
        className="max-h-[28rem] overflow-y-auto rounded-md border"
        totals={{
          label: a.total,
          cells: {
            spend: money(tt.spendMinor, false),
            impressions: n(tt.impressions),
            clicks: n(tt.clicks),
            ctr: formatPercent(tt.ctr, locale, 2),
            leads: n(tt.leads),
            cpl: money(tt.cplMinor, true),
            sales: n(tt.sales),
            cac: money(tt.cacMinor, false),
            revenue: money(tt.revenueMinor, false),
            roas: <Roas value={tt.roas} coverage={tt.revenueCoverage} />,
          },
        }}
      />
      <ul className="space-y-1 text-xs text-muted-foreground">
        <li>{a.unattributed(n(data.unattributed.leads), n(data.unattributed.sales))}</li>
        {tt.revenueCoverage !== null && tt.revenueCoverage < 1 ? (
          <li>{a.coverage(formatPercent(tt.revenueCoverage, locale, 0))}</li>
        ) : null}
        {anyUnknown ? <li>{a.unknownLeads}</li> : null}
        {data.cohortMaturity.immature ? <li>{a.cohortNote}</li> : null}
        {data.filtersIgnored ? <li>{a.filtersIgnored}</li> : null}
        {data.excludedCurrencies.length > 0 ? (
          <li>{a.excluded(data.excludedCurrencies.join(', '))}</li>
        ) : null}
      </ul>
    </div>
  );
}

/**
 * CPL and CAC per ad set as two small bar columns, each on its own scale: a cost per sale is many
 * times a cost per lead, and one shared axis would flatten the CPL bars into nothing.
 */
function CostBars({ rows, currency }: { rows: readonly Row[]; currency: string }) {
  const t = useT();
  const locale = useLocale();
  const a = t.home.ads;
  const top = [...rows].sort((x, y) => y.spendMinor - x.spendMinor).slice(0, 10);
  const maxCpl = Math.max(1, ...top.map((r) => r.cplMinor ?? 0));
  const maxCac = Math.max(1, ...top.map((r) => r.cacMinor ?? 0));

  const bar = (value: number | null, max: number, color: string, cents: boolean) => (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 rounded-full bg-muted" aria-hidden="true">
        {value !== null ? (
          <div
            className="h-2 rounded-full"
            style={{ width: `${Math.max(2, (value / max) * 100)}%`, background: color }}
          />
        ) : null}
      </div>
      <span className="w-16 shrink-0 text-right text-xs tabular-nums">
        {formatMoney(value, currency, locale, { cents })}
      </span>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex gap-4 text-xs text-muted-foreground">
        <LegendKey color={CPL_COLOR} label={a.columns.cpl} />
        <LegendKey color={CAC_COLOR} label={a.columns.cac} />
      </div>
      <ul className="grid gap-x-10 gap-y-3 lg:grid-cols-2">
        {top.map((r) => (
          <li key={r.key} className="space-y-1">
            <p className="truncate text-sm" title={`${r.name} · ${r.campaignName}`}>
              {r.name}
            </p>
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-4">
              {bar(r.cplMinor, maxCpl, CPL_COLOR, true)}
              {bar(r.cacMinor, maxCac, CAC_COLOR, false)}
            </div>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">{a.chartNote}</p>
    </div>
  );
}
